import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./TeamManagement.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { generalActions } from "../../store/actions";
import searchService from "../../store/services/search.service";
import clientService from "../../store/services/clients.service";
import projectService from "../../store/services/projects.service";
import scopeService from "../../store/services/scope.service";
import {
  TeachingPopover,
  Avatar,
  Button,
  TeachingPopoverBody,
  TeachingPopoverSurface,
  TeachingPopoverTrigger,
  Tooltip,
  Switch,
} from "@fluentui/react-components";
import { X, Plus, Loader2, UserRoundCog, ChevronLeft, UserPlus, Pencil, Check } from "lucide-react";
import Input from "../controls/input/customInput/CustomInput";
import Spin from "../controls/spinner/Spin";
import HoverCard from "../hoverCard/HoverCard";
import { getAccountCurrency } from "../../utils/systemSettings";
import { UsersIcon } from "../../views/dashboard/icons/icons";
import Searchable from "../../components/controls/select/CustomSelect";
import { getEditPermission, getLabelByType } from "../../helpers/utils";
import { debounce } from "lodash";
import { ClockDismiss } from "../../modules/default-svg";

// Utilization pill shown next to user name
function UtilizationPill({ maxWeeklyHours, availableWeeklyHours }) {
  if (maxWeeklyHours == null || maxWeeklyHours === 0) return null;
  const utilized = Math.round(((maxWeeklyHours - availableWeeklyHours) / maxWeeklyHours) * 100);
  const clampedUtil = Math.max(utilized, 0);
  const consumedHours = maxWeeklyHours - (availableWeeklyHours ?? 0);

  const getColor = (pct) => {
    if (pct >= 100) return styles.pillRed;
    if (pct >= 80) return styles.pillOrange;
    return styles.pillGreen;
  };

  return (
    <Tooltip
      content={`${consumedHours}/${maxWeeklyHours}h utilized`}
      relationship="label"
    >
      <span className={`${styles.utilizationPill} ${getColor(clampedUtil)}`}>
        {clampedUtil}%
      </span>
    </Tooltip>
  );
}

// Inline form for project resource assignment / editing
function ProjectResourceForm({ isEditMode = false, user, projectId, onConfirm, onCancel, isLoading }) {
  const settings = JSON.parse(localStorage.getItem("settings"));
  const currency = getAccountCurrency(settings);

  const [hourlyRate, setHourlyRate] = useState("");
  const [hourlyBillRate, setHourlyBillRate] = useState("");
  const [maxWeeklyHours, setMaxWeeklyHours] = useState("");
  const [isTimesheetEntryAllowed, setIsTimesheetEntryAllowed] = useState(false);
  const [selectedCashflowLine, setSelectedCashflowLine] = useState(null);
  const [cashflowLineOptions, setCashflowLineOptions] = useState([]);
  // Sync form fields whenever user data changes
  useEffect(() => {
    if (!user) return;
    setHourlyRate(user.hourlyRate != null ? String(Number(user.hourlyRate)) : "0");
    setHourlyBillRate(user.hourlyBillRate != null ? String(Number(user.hourlyBillRate)) : "0");
    setMaxWeeklyHours(user.maxWeeklyHours != null ? String(Number(user.maxWeeklyHours)) : "0");
    setIsTimesheetEntryAllowed(user.isTimesheetEntryAllowed);
    if (user.cashflowLine) {
      setSelectedCashflowLine({
        label: `${getLabelByType("cashflowLineCategory", user.cashflowLine.category)} - ${user.cashflowLine.description}`,
        value: user.cashflowLine.id});
      setCashflowLineOptions([{
        id: user.cashflowLine.id,
        description: user.cashflowLine.description,
        category: user.cashflowLine.category
      }]);
    }

  }, [user]);

  const handleConfirm = () => {
    if (!selectedCashflowLine)
      return;

    onConfirm({
      ...(!isEditMode && { hourlyRate: parseFloat(hourlyRate) || 0 }),
      ...(!isEditMode && { hourlyBillRate: parseFloat(hourlyBillRate) || 0 }),
      maxWeeklyHours: parseFloat(maxWeeklyHours) || 0,
      isTimesheetEntryAllowed,
      cashflowLineId: selectedCashflowLine.value
    });
    setCashflowLineOptions([]);
  };

  const handleCancel = () => {
    onCancel();
    setCashflowLineOptions([]);
  };
  const searchCashflowLineHandler = async (value, projectId) => {
    setCashflowLineOptions([]);
    if (!value) value = "";

    const searchParams = {
      description: value,
    };

    try {
      const response = await projectService.getProjectCashflowLookup(projectId, searchParams);
      setCashflowLineOptions(response?.lines || []);
    } catch (error) {
      console.error("Error fetching cashflow lines:", error);
      setCashflowLineOptions([]);
    }
  };

  const cashflowLineSearchDebounced = useRef(
    debounce((value, projectId) => searchCashflowLineHandler(value, projectId), 500)
  );

  const handleCashflowLineSearch = useCallback((value, projectId) => {
    cashflowLineSearchDebounced.current(value, projectId);
  }, []);

  const handleCashflowLineSelect = (line) => {
    setSelectedCashflowLine(line || null);
  };

  return (
    <div className={styles.resourceForm}>
      <div className={styles.resourceFormUser}>
        <Avatar
          color="colorful"
          name={user?.name}
          image={{ src: user?.image }}
          size={28}
        />
        <div className={styles.userNameGroup}>
          <span className={styles.resourceFormUserName}>{user?.name}</span>
          {user?.title && <span className={styles.userTitle}>{user.title}</span>}
        </div>
      </div>
      <div className={styles.resourceFormFields}>
        {/* Rates row - side by side */}
        <div className={styles.resourceFormRow}>
          <div className={styles.resourceFormField}>
            <label className={styles.resourceFormLabel}>Hourly Rate</label>
            <div className={styles.resourceFormInputWrapper}>
              <span className={styles.currencyPrefix}>{currency}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`${styles.resourceFormInput} ${styles.resourceFormInputWithPrefix} ${isEditMode ? styles.disabled : ''}`}
                value={hourlyRate}
                disabled={isEditMode}
                onChange={(e) => !isEditMode && setHourlyRate(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.resourceFormField}>
            <label className={styles.resourceFormLabel}>Bill Rate</label>
            <div className={styles.resourceFormInputWrapper}>
              <span className={styles.currencyPrefix}>{currency}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`${styles.resourceFormInput} ${styles.resourceFormInputWithPrefix} ${isEditMode ? styles.disabled : ''}`}
                value={hourlyBillRate}
                disabled={isEditMode}
                onChange={(e) => !isEditMode && setHourlyBillRate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Max weekly hours - full width */}
        <div className={styles.resourceFormField}>
          <label className={styles.resourceFormLabel}>Max Weekly Hours</label>
          <input
            type="number"
            min="0"
            step="0.5"
            className={styles.resourceFormInput}
            value={maxWeeklyHours}
            onChange={(e) => setMaxWeeklyHours(e.target.value)}
          />
        </div>
        {/* Timesheet entry allowed toggle */}
        <div className={styles.resourceInlineFormField}>
          <Switch
            checked={isTimesheetEntryAllowed}
            onChange={(ev, data) => {
              ev.stopPropagation();
              setIsTimesheetEntryAllowed(data.checked);
            }}
            className={`ToggleStyle`}
          />
          <span className={styles.resourceFormLabel}>Allow Timesheet Entry</span>
        </div>
        {/* Cashflow Code - full width */}

        <div className={styles.resourceFormField}>
          <label className={styles.resourceFormLabel}>Code</label>
          <Searchable
            isRequired
            placeholder="Search code..."
            SelectText="Code"
            searchText={(value) => handleCashflowLineSearch(value, projectId)}
            options={cashflowLineOptions.map((option) => ({
              label: `${getLabelByType("cashflowLineCategory", option.category)} - ${option.description}`,
              value: option.id,
            }))}
            defaultVal={selectedCashflowLine}
            onChange={(line) => handleCashflowLineSelect(line)}
            className={styles.resourceFormInput}
            menuPortalTarget={null}
          />
        </div>

      </div>
      <div className={styles.resourceFormActions}>
        <Button
          className={styles.cancelFormBtn}
          onClick={handleCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          className={styles.confirmFormBtn}
          onClick={handleConfirm}
          disabled={isLoading || !selectedCashflowLine}
        >
          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Confirm</>}
        </Button>
      </div>
    </div >
  );
}

export default function TeamManagement({
  entityType,
  entityId,
  parentId,
  initialTeamUsers = [],
  disabled = false,
  onTeamUpdate,
}) {
  const [openPopover, setIsPopoverOpen] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [filteredTeamUsers, setFilteredTeamUsers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isAssignMode, setIsAssignMode] = useState(false);

  // Project-specific form state
  const [pendingUser, setPendingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null); // holds full API response data when set
  const [isFetchingEditUser, setIsFetchingEditUser] = useState(false);

  const dispatch = useDispatch();
  const searchTimeoutRef = useRef(null);
  const pageSize = 10;

  const entityLabel =
    entityType === "client" ? "Client" : entityType === "project" ? "Project" : "Scope";
  const isProject = entityType?.toLowerCase() === "project";
  const isClient = entityType?.toLowerCase() === "client";

  useEffect(() => {
    const sorted = [...initialTeamUsers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setTeamUsers(sorted);
    setFilteredTeamUsers(sorted);
    setHasChanges(false);
  }, [initialTeamUsers]);

  useEffect(() => {
    if (teamSearchTerm.trim()) {
      const filtered = teamUsers.filter((user) =>
        user.name.toLowerCase().includes(teamSearchTerm.toLowerCase())
      );
      setFilteredTeamUsers(filtered);
    } else {
      setFilteredTeamUsers(teamUsers);
    }
  }, [teamSearchTerm, teamUsers]);

  const canManage = () => {
    switch (entityType.toLowerCase()) {
      case "client":
        return getEditPermission("Client");
      case "project":
        return getEditPermission("Project");
      case "scope":
        return getEditPermission("Scope");
      default:
        return false;
    }
  };

  const getApiService = () => {
    switch (entityType.toLowerCase()) {
      case "client":
        return {
          add: (userId) => clientService.AddResourceToClient(entityId, userId),
          remove: (userId) => clientService.RemoveResourceFromClient(entityId, userId),
        };
      case "project":
        return {
          add: (userId, body) => projectService.AddResourceToProject(entityId, userId, body),
          remove: (userId) => projectService.RemoveResourceFromProject(entityId, userId),
          update: (userId, body) => projectService.UpdateResourceInProject(entityId, userId, body),
        };
      case "scope":
        return {
          add: (userId) => scopeService.AddResourceToScope(entityId, userId),
          remove: (userId) => scopeService.RemoveResourceFromScope(entityId, userId),
        };
      default:
        throw new Error(`Invalid entity type: ${entityType}`);
    }
  };

  const filterSearchResults = (results) => {
    const teamUserIds = teamUsers.map((u) => u.id);
    return results.filter((u) => !teamUserIds.includes(u.id));
  };

  const searchUsers = async (search = "", page = 1, append = false) => {
    if (!append) {
      setIsSearching(true);
      setSearchResults([]);
    } else {
      setIsLoadingMore(true);
    }

    try {
      let queryParams = { pageSize, pageNumber: page };
      let isForClientAssignement = false;
      switch (entityType.toLowerCase()) {
        case "client":
          isForClientAssignement = true;
          break;
        case "project":
          if (!parentId) return;
          queryParams.clientId = parentId;
          break;
        case "scope":
          if (!parentId) return;
          queryParams.projectId = parentId;
          break;
        default:
          throw new Error(`Invalid entity type: ${entityType}`);
      }

      const response = isForClientAssignement
        ? await searchService.UsersForClientAssignment(entityId, search)
        : await searchService.UsersForAssignment(search, queryParams);

      const pagination = response.headers["x-pagination"]
        ? JSON.parse(response.headers["x-pagination"])
        : null;

      const filteredUsers = filterSearchResults(response.data || []);

      if (append) {
        setSearchResults((prev) => [...prev, ...filteredUsers]);
      } else {
        setSearchResults(filteredUsers);
      }

      setHasMore(pagination?.HasNext || false);
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.data?.message || "Failed to search users",
          type: "error",
        })
      );
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  };

  const debouncedSearch = (value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPageNumber(1);
      searchUsers(value, 1, false);
    }, 500);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (value.trim()) {
      debouncedSearch(value);
    } else {
      setSearchResults([]);
      setHasMore(false);
    }
  };

  const handleTeamSearchChange = (value) => setTeamSearchTerm(value);

  // For project: open the inline form; for others: add directly
  const handleAddUser = (user) => {
    if (isProject) {
      setPendingUser(user);
    } else {
      addUserDirectly(user);
    }
  };

  const addUserDirectly = async (user, body) => {
    setLoadingUserId(user.id);
    try {
      const apiService = getApiService();
      await apiService.add(user.id, body);

      const updatedTeam = [...teamUsers, user];
      setTeamUsers(updatedTeam);
      setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
      setHasChanges(true);
      setPendingUser(null);

      dispatch(
        generalActions.showToast({
          title: `${user.name} added to team successfully`,
          type: "success",
        })
      );
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.data?.message || `Failed to add ${user.name} to team.`,
          type: "error",
        })
      );
    } finally {
      setLoadingUserId(null);
    }
  };

  const handleConfirmProjectAdd = async (formValues) => {
    if (!pendingUser) return;
    await addUserDirectly(pendingUser, formValues);
  };

  const handleCancelProjectForm = () => {
    setPendingUser(null);
    setEditingUser(null);
  };

  const handleEditUser = async (user) => {
    setIsFetchingEditUser(true);
    setEditingUser({
      name: user.name,
      title: user.title,
      image: user.image,
      id: user.id,
      cashflowLine: user.cashflowLine
    }); // set basic info immediately for form display while fetching rest of data
    try {
      const response = await projectService.GetResourceFromProject(entityId, user.id);
      const data = response?.data ?? response; // handle both {data: ...} and direct response shapes
      if (!data) throw new Error("Empty response from server");
      setEditingUser(data);
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.data?.message || error?.message || `Failed to load resource data for ${user.name}`,
          type: "error",
        })
      );
      setEditingUser(null);
    } finally {
      setIsFetchingEditUser(false);
    }
  };

  const handleConfirmProjectEdit = async (formValues) => {
    if (!editingUser) return;
    setLoadingUserId(editingUser.id);
    try {
      const apiService = getApiService();
      await apiService.update(editingUser.id, formValues);

      const updatedTeam = teamUsers.map((u) =>
        u.id === editingUser.id
          ? { ...u, ...formValues, maxWeeklyHours: formValues.maxWeeklyHours, availableWeeklyHours: formValues.maxWeeklyHours }
          : u
      );
      setTeamUsers(updatedTeam);
      setHasChanges(true);
      setEditingUser(null);

      dispatch(
        generalActions.showToast({
          title: `${editingUser.name} updated successfully`,
          type: "success",
        })
      );
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.data?.message || `Failed to update ${editingUser.name}`,
          type: "error",
        })
      );
    } finally {
      setLoadingUserId(null);
    }
  };

  const handleRemoveUser = async (user) => {
    setRemovingUserId(user.id);
    try {
      const apiService = getApiService();
      const response = await apiService.remove(user.id);

      if (response?.isAssigned) {
        dispatch(
          generalActions.showToast({
            title: `${user.name} cannot be removed as they are assigned to active tasks`,
            type: "error",
          })
        );
        setRemovingUserId(null);
        return;
      }

      const updatedTeam = teamUsers.filter((u) => u.id !== user.id);
      setTeamUsers(updatedTeam);
      setHasChanges(true);

      dispatch(
        generalActions.showToast({
          title: `${user.name} removed from team successfully`,
          type: "success",
        })
      );
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.data?.message || `Failed to remove ${user.name} from team`,
          type: "error",
        })
      );
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleLoadMore = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    searchUsers(searchTerm, nextPage, true);
  };

  const handleOpenPopover = () => {
    setSearchTerm("");
    setTeamSearchTerm("");
    setSearchResults([]);
    setIsAssignMode(false);
    setPendingUser(null);
    setEditingUser(null);
    setIsPopoverOpen(true);
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
    setIsAssignMode(false);
    setPendingUser(null);
    setEditingUser(null);
    if (hasChanges && onTeamUpdate) {
      onTeamUpdate();
      setHasChanges(false);
    }
  };

  const handleToggleAssignMode = () => {
    const newAssignMode = !isAssignMode;
    setIsAssignMode(newAssignMode);
    setSearchTerm("");
    setTeamSearchTerm("");
    setSearchResults([]);
    setPendingUser(null);
    setEditingUser(null);

    if (newAssignMode) {
      setPageNumber(1);
      searchUsers("", 1, false);
    }
  };

  return (
    <div className={styles.teamManagementWrapper}>
      <TeachingPopover
        positioning="below-start"
        withArrow={false}
        open={openPopover}
        onOpenChange={(event, data) => {
          if (!disabled) {
            if (data.open) {
              handleOpenPopover();
            } else {
              handleClosePopover();
            }
          }
        }}
      >
        {
          canManage() && (
            <TeachingPopoverTrigger>
              <Tooltip content={"Manage resources"} relationship="label">
                <Button
                  className={styles.manageButton}
                  onClick={handleOpenPopover}
                  disabled={disabled}
                >
                  <UserRoundCog size={16} />
                  <span className={styles.buttonLabel}>Manage</span>
                </Button>
              </Tooltip>
            </TeachingPopoverTrigger>
          )
        }


        <TeachingPopoverSurface className={styles.popoverSurface}>
          <div className={styles.popoverHeader}>
            {isAssignMode ? (
              <>
                <Button className={styles.backButton} onClick={handleToggleAssignMode}>
                  <ChevronLeft size={16} />
                  <span>Back</span>
                </Button>
                <h3 className={styles.popoverTitle}>Assign Resources</h3>
              </>
            ) : (
              <>
                <h3 className={styles.popoverTitle}>{entityLabel} Team</h3>
                <Tooltip content={"Assign resources"} relationship="label">
                  <Button className={styles.assignButton} onClick={handleToggleAssignMode}>
                    <UserPlus size={16} />
                    <span>Assign</span>
                  </Button>
                </Tooltip>
              </>
            )}
          </div>

          <TeachingPopoverBody className={styles.popoverBody}>
            {isAssignMode ? (
              <>
                {/* If a pending user is selected for project, show inline form */}
                {pendingUser ? (
                  <ProjectResourceForm
                    user={pendingUser}
                    projectId={entityId}
                    onConfirm={handleConfirmProjectAdd}
                    onCancel={handleCancelProjectForm}
                    isLoading={loadingUserId === pendingUser.id}
                  />
                ) : (
                  <>
                    <div className={styles.searchWrapper}>
                      <Input
                        placeholder="Search users to assign"
                        handleChange={handleSearchChange}
                        value={searchTerm}
                      />
                    </div>

                    <h4 className={styles.sectionTitle}>Search Results</h4>

                    {isSearching && searchResults.length === 0 ? (
                      <div className={styles.loadingWrapper}>
                        <Spin />
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className={styles.userListWrapper}>
                        {searchResults.map((user) => (
                          <div key={user.id} className={styles.userRow}>
                            <div className={styles.userInfo}>
                              <HoverCard
                                HoverCardTrigger={
                                  <Avatar
                                    color="colorful"
                                    name={user.name}
                                    image={{ src: user.image }}
                                    size={32}
                                  />
                                }
                                AvatarId={user.id}
                                AvatarName={user.name}
                                AvatarImage={user.image}
                                AvatarDesignation={user.title}
                                AvatarEmail={user.email}
                                AvatarPhone={user.phoneNumber}
                                AvatarIsExternal={user.isExternal}
                              />
                              <div className={styles.userNameGroup}>
                                <p className={styles.userName}>{user.name}</p>
                                {user.title && <p className={styles.userTitle}>{user.title}</p>}
                              </div>
                            </div>
                            {(isClient || isProject) && (
                              <UtilizationPill
                                maxWeeklyHours={user.maxWeeklyHours}
                                availableWeeklyHours={user.availableWeeklyHours}
                              />
                            )}
                            <button
                              className={`${styles.actionButton} ${styles.addButton}`}
                              onClick={() => handleAddUser(user)}
                              disabled={loadingUserId === user.id}
                            >
                              {loadingUserId === user.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Tooltip content={"Assign resource"} relationship="label">
                                  <Plus size={16} />
                                </Tooltip>
                              )}
                            </button>
                          </div>
                        ))}

                        {hasMore && (
                          <div className={styles.loadMoreRow}>
                            {isLoadingMore ? (
                              <Spin />
                            ) : (
                              <Button onClick={handleLoadMore} className={styles.loadMoreBtn}>
                                Load more
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}>
                          <UsersIcon />
                        </div>
                        <p className={styles.emptyStateText}>
                          {searchTerm ? "No resource found" : "Start typing to search users"}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {/* If editing a project team member */}
                {editingUser ? (
                  isFetchingEditUser ? (
                    <div className={styles.editLoadingWrapper}>
                      <div className={styles.editLoadingUser}>
                        <Avatar
                          color="colorful"
                          name={editingUser.name}
                          image={{ src: editingUser.image }}
                          size={28}
                        />
                        <div className={styles.userNameGroup}>
                          <span className={styles.editLoadingName}>{editingUser.name}</span>
                          {editingUser.title && <p className={styles.editLoadingTitle}>{editingUser.title}</p>}
                        </div>
                      </div>
                      <Spin />
                    </div>
                  ) : editingUser.hourlyRate != null ? (
                    <ProjectResourceForm
                      user={editingUser}
                      isEditMode={true}
                      projectId={entityId}
                      onConfirm={handleConfirmProjectEdit}
                      onCancel={handleCancelProjectForm}
                      isLoading={loadingUserId === editingUser.id}
                    />
                  ) : (
                    // Fallback: data came back but fields missing — show form with zeros
                    <ProjectResourceForm
                      user={{ ...editingUser, hourlyRate: 0, hourlyBillRate: 0, maxWeeklyHours: 0, iswTimesheetEntryAllowed: false }}
                      isEditMode={true}
                      projectId={entityId}
                      onConfirm={handleConfirmProjectEdit}
                      onCancel={handleCancelProjectForm}
                      isLoading={loadingUserId === editingUser.id}
                    />
                  )
                ) : (
                  <>
                    <div className={styles.searchWrapper}>
                      <Input
                        placeholder="Search team members"
                        handleChange={handleTeamSearchChange}
                        value={teamSearchTerm}
                      />
                    </div>

                    <h4 className={styles.sectionTitle}>
                      Team Members ({filteredTeamUsers.length})
                    </h4>

                    {filteredTeamUsers.length > 0 ? (
                      <div className={styles.userListWrapper}>
                        {filteredTeamUsers.map((user) => (
                          <div key={user.id} className={styles.userRow}>
                            <div className={styles.userInfo}>
                              <HoverCard
                                HoverCardTrigger={
                                  <Avatar
                                    color="colorful"
                                    name={user.name}
                                    image={{ src: user.image }}
                                    size={32}
                                  />
                                }
                                AvatarId={user.id}
                                AvatarName={user.name}
                                AvatarImage={user.image}
                                AvatarDesignation={user.title}
                                AvatarEmail={user.email}
                                AvatarPhone={user.phoneNumber}
                                AvatarIsExternal={user.isExternal}
                              />
                              <div className={styles.userNameGroup}>
                                <p className={styles.userName}>{user.name}</p>
                                {user.title && <p className={styles.userTitle}>{user.title}</p>}
                              </div>

                            </div>
                            <div className={styles.rowActions}>
                              {isProject && (
                                <>
                                  {
                                    !user.isTimesheetEntryAllowed && (
                                      <ClockDismiss color="#342c82" tooltip={"Timesheet entry not allowed"} />
                                    )
                                  }

                                  <Tooltip
                                    content={`${user.maxWeeklyHours ?? 0} h/week capacity`}
                                    relationship="label"
                                  >
                                    <span className={`${styles.utilizationPill} ${styles.pillPurple}`}>
                                      {user.maxWeeklyHours ?? 0} h
                                    </span>
                                  </Tooltip>
                                  <button
                                    className={`${styles.actionButton} ${styles.editButton}`}
                                    onClick={() => handleEditUser(user)}
                                    disabled={removingUserId === user.id}
                                  >
                                    <Tooltip content={"Edit Resource Assignment"} relationship="label">
                                      <Pencil size={14} />
                                    </Tooltip>
                                  </button>
                                </>

                              )}
                              <button
                                className={`${styles.actionButton} ${styles.removeButton}`}
                                onClick={() => handleRemoveUser(user)}
                                disabled={removingUserId === user.id}
                              >
                                {removingUserId === user.id ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Tooltip content={"Remove resource"} relationship="label">
                                    <X size={16} />
                                  </Tooltip>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}>
                          <UsersIcon />
                        </div>
                        <p className={styles.emptyStateText}>
                          {teamSearchTerm
                            ? "No team members match your search"
                            : "No team members yet"}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </TeachingPopoverBody>
        </TeachingPopoverSurface>
      </TeachingPopover>
    </div>
  );
}
